import { motion, Variants } from 'framer-motion';
import { useIsMobile } from "@/hooks/use-mobile";

interface ImageRevealProps {
  leftImage: string;
  middleImage: string;
  rightImage: string;
}

const ImageReveal = ({ leftImage, middleImage, rightImage }: ImageRevealProps) => {
  const isMobile = useIsMobile();
  
  const containerVariants: Variants = {
    initial: {
      opacity: 0,
    },
    animate: {
      opacity: 1,
      transition: {
        delay: 1.4,
        staggerChildren: 0.2,
      }
    }
  };

  const leftImageVariants: Variants = {
    initial: { rotate: 0, x: 0, y: 0 },
    animate: {
      rotate: -8,
      x: isMobile ? -60 : -180,
      y: isMobile ? 5 : 20,
      transition: {
        type: "spring",
        stiffness: 120,
        damping: 12
      }
    },
    hover: {
      rotate: 1,
      x: isMobile ? -70 : -180,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 200,
        damping: 15
      }
    }
  };

  const middleImageVariants: Variants = {
    initial: { rotate: 0, x: 0, y: 0 },
    animate: {
      rotate: 6,
      x: 0,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 120,
        damping: 12
      }
    },
    hover: {
      rotate: 0,
      x: 0,
      y: -10,
      transition: {
        type: "spring",
        stiffness: 200,
        damping: 15
      }
    }
  };

  const rightImageVariants: Variants = {
    initial: { rotate: 0, x: 0, y: 0 },
    animate: {
      rotate: -6,
      x: isMobile ? 70 : 225,
      y: isMobile ? 10 : 30,
      transition: {
        type: "spring",
        stiffness: 120,
        damping: 12
      }
    },
    hover: {
      rotate: 3,
      x: isMobile ? 80 : 230,
      y: isMobile ? 5 : 10,
      transition: {
        type: "spring",
        stiffness: 200,
        damping: 15
      }
    }
  };

  return (
    <motion.div
      className="relative flex items-center justify-center w-full h-48 sm:h-56 md:h-72 lg:h-80 my-4 md:my-1"
      variants={containerVariants}
      initial="initial"
      animate="animate"
    >
      {/* Left Image - Lowest z-index */}
      <motion.div
        className="absolute w-32 h-32 sm:w-40 sm:h-40 md:w-48 md:h-48 lg:w-56 lg:h-56 origin-bottom-right overflow-hidden rounded-xl shadow-xl bg-white p-2"
        variants={leftImageVariants}
        whileHover="hover"
        animate="animate"
        style={{ zIndex: 30 }}
      >
        <img
          src={leftImage}
          alt="Fotografía histórica del cine mexicano"
          className="w-full h-full object-cover rounded-lg"
        />
      </motion.div>

      {/* Middle Image - Middle z-index */}
      <motion.div
        className="absolute w-32 h-32 sm:w-40 sm:h-40 md:w-48 md:h-48 lg:w-56 lg:h-56 origin-bottom-left overflow-hidden rounded-xl shadow-xl bg-white p-2"
        variants={middleImageVariants}
        whileHover="hover"
        animate="animate"
        style={{ zIndex: 20 }}
      >
        <img
          src={middleImage}
          alt="Premios Ariel del cine mexicano"
          className="w-full h-full object-cover rounded-lg"
        />
      </motion.div>

      {/* Right Image - Highest z-index */}
      <motion.div
        className="absolute w-32 h-32 sm:w-40 sm:h-40 md:w-48 md:h-48 lg:w-56 lg:h-56 origin-bottom-right overflow-hidden rounded-xl shadow-xl bg-white p-2"
        variants={rightImageVariants}
        whileHover="hover"
        animate="animate"
        style={{ zIndex: 10 }}
      >
        <img
          src={rightImage}
          alt="Retrato histórico del cine mexicano"
          className="w-full h-full object-cover rounded-lg"
        />
      </motion.div>
    </motion.div>
  );
};

export default ImageReveal;
